import React, { useState, useEffect } from 'react';
import './Etape1.css';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';

function Etape1({ mission, onBack, onSave }) {
  const [formData, setFormData] = useState({
    activite: mission.activite || 'electricite',
    client_id: mission.client_id || '',
    client_nom: mission.client_nom || '',
    etablissement_id: mission.etablissement_id || '',
    etablissement: mission.etablissement || '',
    site_adresse: mission.site_adresse || '',
    code_postal_etablissement: mission.code_postal_etablissement || '',
    ville_etablissement: mission.ville_etablissement || '',
    activite_etablissement: mission.activite_etablissement || '',
    date_verification: mission.date_affectation || '',
    type_verification: mission.type_verification || '',
    type_rapport_temporaire: 'complet',
    inclure_q18: false,
    inclure_erp: false,
    inspecteur_id: mission.inspecteur_id || '',
    verificateur: mission.verificateur || '',
    date_rapport: '',
    numero_rapport: mission.numero_rapport || '',
    maitre_ouvrage: '',
    proprietaire_etablissement: '',
    exploitant: '',
    periodicite_reglementaire: '',
    periodicite_contractuelle: '',
    duree_intervention: '',
    modele_texte: '',
    renseignements_complementaires: '',
    nom_contact: '',
    email_contact: '',
    telephone_contact: ''
  });

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const activiteOptions = [
    { value: 'electricite', label: '⚡ Électricité' },
    { value: 'levage', label: '🏗️ Appareils de levage' },
    { value: 'incendie', label: '🔥 Sécurité incendie' },
    { value: 'thermographie', label: '🌡️ Thermographie' }
  ];

  const isTemporaire = formData.type_verification === "Vérification avant mise en service des installations temporaires en application de l'article R 4226-21 du Code du Travail";
  const isRVRAT = formData.type_verification === "Vérification après travaux ERP 1er groupe (RVRAT)";

  useEffect(() => {
    loadSavedData();
  }, []);

  const loadSavedData = async () => {
    setLoading(true);
    try {
      const saved = await inspectionStorage.getStep(mission.id, 'etape_1_infos_generales');
      if (saved) {
        setFormData(prev => ({ ...prev, ...saved }));
        console.log('📦 Données Étape 1 récupérées depuis le cache');
      }

      // ✅ Compléter automatiquement l'activité établissement si manquante
      const meta = await inspectionStorage.getStep(mission.id, 'meta');
      const fallbackActivity =
        saved?.activite_etablissement ||
        mission.activite_etablissement ||
        meta?.activite_etablissement ||
        meta?.etablissement_data?.activite ||
        '';
      if (fallbackActivity) {
        setFormData(prev => ({
          ...prev,
          activite_etablissement: prev.activite_etablissement || fallbackActivity
        }));
      }
    } catch (error) {
      console.error('❌ Erreur chargement cache:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const stepData = {
        ...formData,
        timestamp: new Date().toISOString()
      };
      
      await inspectionStorage.saveStep(mission.id, 'etape_1_infos_generales', stepData);
      syncManager.syncStep(mission.id, 'etape_1_infos_generales', stepData);
      
      console.log('✅ Étape 1 sauvegardée localement');
      
      if (onSave) {
        onSave(formData);
      }

      alert('✅ Étape 1 sauvegardée avec succès !');
      onBack();
      
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="etape-loading">
        <div className="spinner-large"></div>
        <p>Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className="etape-container">
      {/* Header */}
      <div className="etape-header">
        <button className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <div className="etape-title">
          <h1>Étape 1/14</h1>
          <p>Informations générales</p>
        </div>
        <div></div>
      </div>

      {/* Content */}
      <div className="etape-content">
        
        {/* Section: Récapitulatif Mission (lecture seule) */}
        <div className="form-section">
          <h2>📋 Récapitulatif de la mission</h2>
          
          <div className="mission-recap">
            <div className="recap-item">
              <span className="recap-label">Client</span>
              <span className="recap-value">{mission.client_nom || 'Non défini'}</span>
            </div>
            <div className="recap-item">
              <span className="recap-label">Établissement</span>
              <span className="recap-value">{mission.etablissement || formData.etablissement || 'Non défini'}</span>
            </div>
            <div className="recap-item">
              <span className="recap-label">Adresse</span>
              <span className="recap-value">{mission.site_adresse || formData.site_adresse || 'Non définie'}</span>
            </div>
            <div className="recap-item">
              <span className="recap-label">Vérificateur</span>
              <span className="recap-value">{mission.verificateur || 'Non assigné'}</span>
            </div>
            <div className="recap-item">
              <span className="recap-label">N° Rapport</span>
              <span className="recap-value">{mission.numero_rapport}</span>
            </div>
          </div>
        </div>

        {/* Section: Type d'activité */}
        <div className="form-section">
          <h2>⚡ Type d'activité</h2>
          
          <div className="form-group">
            <label className="required">Type d'activité</label>
            <select
              value={formData.activite}
              onChange={(e) => updateField('activite', e.target.value)}
              className="form-select"
            >
              {activiteOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Section: Vérification */}
        <div className="form-section">
          <h2>🔍 Paramètres de vérification</h2>

          <div className="form-group">
            <label className="required">Date de la vérification</label>
            <input
              type="date"
              value={formData.date_verification}
              onChange={(e) => updateField('date_verification', e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="required">Type de vérification</label>
            <select
              value={formData.type_verification}
              onChange={(e) => updateField('type_verification', e.target.value)}
              className="form-select"
            >
              <option value="">Sélectionnez un type</option>
              <option value="Vérification initiale des installations électriques">
                Vérification initiale des installations électriques.
              </option>
              <option value="Vérification périodique des installations électriques">
                Vérification périodique
              </option>
              <option value="Vérification sur mise en demeure des installations électriques">
                Vérification après modification
              </option>
              <option value="Vérification avant mise en service des installations temporaires en application de l'article R 4226-21 du Code du Travail">
                Vérification installations temporaires (R 4226-21)
              </option>
              <option value="Vérification sur mise en demeure à la demande de l'inspection du travail en application de l'article R4722-26 du Code du Travail">
                Vérification sur mise en demeure - inspection du travail (R4722-26)
              </option>
              <option value="Vérification après travaux ERP 1er groupe (RVRAT)">
                Vérification après travaux ERP (RVRAT)
              </option>
            </select>
          </div>

          {!isRVRAT && (
            <div className="form-group">
              {isTemporaire ? (
                <>
                  <label className="required">Type de rapport</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="type_rapport"
                        value="complet"
                        checked={formData.type_rapport_temporaire === 'complet'}
                        onChange={(e) => updateField('type_rapport_temporaire', e.target.value)}
                      />
                      <div>
                        <strong>Rapport complet</strong>
                        <p>Toutes les étapes (menu 14 étapes)</p>
                      </div>
                    </label>

                    <label className="radio-label">
                      <input
                        type="radio"
                        name="type_rapport"
                        value="simplifie"
                        checked={formData.type_rapport_temporaire === 'simplifie'}
                        onChange={(e) => updateField('type_rapport_temporaire', e.target.value)}
                      />
                      <div>
                        <strong>Rapport simplifié</strong>
                        <p>Étapes réduites (menu 11 étapes)</p>
                      </div>
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <label>Rapports annexes & options</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.inclure_q18}
                        onChange={(e) => updateField('inclure_q18', e.target.checked)}
                      />
                      Inclure un rapport Q18
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.inclure_erp}
                        onChange={(e) => updateField('inclure_erp', e.target.checked)}
                      />
                      Vérification Réglementaire ERP
                    </label>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Section: Dates et Référence */}
        <div className="form-section">
          <h2>📅 Dates et Référence</h2>

          <div className="info-grid">
            <div className="form-group">
              <label>Date du rapport</label>
              <input
                type="date"
                value={formData.date_rapport}
                disabled
                className="form-input disabled"
              />
              <p className="helper-text">Sera définie à la validation finale</p>
            </div>

            <div className="form-group">
              <label>Référence du rapport</label>
              <input
                type="text"
                value={formData.numero_rapport}
                disabled
                className="form-input disabled"
              />
              <p className="helper-text">Automatiquement générée</p>
            </div>
          </div>
        </div>

        {isRVRAT ? (
          // 🔥 SECTION RVRAT - Intervenants
          <div className="form-section">
            <h2>ℹ️ Intervenants du projet</h2>

            <div className="info-grid">
              <div className="form-group">
                <label className="required">Maître d'Ouvrage</label>
                <input
                  type="text"
                  value={formData.maitre_ouvrage}
                  onChange={(e) => updateField('maitre_ouvrage', e.target.value)}
                  placeholder="Nom du maître d'ouvrage"
                  className="form-input"
                />
                <p className="helper-text">Responsable du projet de construction/travaux</p>
              </div>

              <div className="form-group">
                <label className="required">Propriétaire de l'établissement</label>
                <input
                  type="text"
                  value={formData.proprietaire_etablissement}
                  onChange={(e) => updateField('proprietaire_etablissement', e.target.value)}
                  placeholder="Nom du propriétaire"
                  className="form-input"
                />
                <p className="helper-text">Propriétaire du bâtiment ou de l'ERP</p>
              </div>

              <div className="form-group">
                <label className="required">Exploitant</label>
                <input
                  type="text"
                  value={formData.exploitant}
                  onChange={(e) => updateField('exploitant', e.target.value)}
                  placeholder="Nom de l'exploitant"
                  className="form-input"
                />
                <p className="helper-text">Exploitant de l'établissement recevant du public</p>
              </div>
            </div>
          </div>
        ) : (
          // 🔥 SECTION STANDARD - Périodicités et infos complémentaires
          <div className="form-section">
            <h2>ℹ️ Périodicités et informations complémentaires</h2>

            <div className="info-grid">
              <div className="form-group">
                <label>Périodicité réglementaire</label>
                <select
                  value={formData.periodicite_reglementaire}
                  onChange={(e) => updateField('periodicite_reglementaire', e.target.value)}
                  className="form-select"
                >
                  <option value="">Sélectionnez</option>
                  <option value="12_mois">12 mois</option>
                  <option value="24_mois">24 mois</option>
                  <option value="36_mois">36 mois</option>
                  <option value="48_mois">48 mois</option>
                </select>
                <p className="helper-text">Périodicité imposée par la réglementation</p>
              </div>

              <div className="form-group">
                <label>Périodicité contractuelle</label>
                <select
                  value={formData.periodicite_contractuelle}
                  onChange={(e) => updateField('periodicite_contractuelle', e.target.value)}
                  className="form-select"
                >
                  <option value="">Sélectionnez</option>
                  <option value="6_mois">6 mois</option>
                  <option value="12_mois">12 mois</option>
                  <option value="24_mois">24 mois</option>
                  <option value="36_mois">36 mois</option>
                </select>
                <p className="helper-text">Périodicité définie par contrat client</p>
              </div>

              <div className="form-group">
                <label>Durée d'intervention</label>
                <input
                  type="text"
                  value={formData.duree_intervention}
                  onChange={(e) => updateField('duree_intervention', e.target.value)}
                  placeholder="Ex: 2h30, 4h, 1 jour..."
                  className="form-input"
                />
                <p className="helper-text">Durée estimée ou réelle</p>
              </div>
            </div>

            <div className="form-group">
              <label>Modèle de texte</label>
              <select
                value={formData.modele_texte}
                onChange={(e) => {
                  updateField('modele_texte', e.target.value);
                  if (e.target.value === 'erp') {
                    updateField('renseignements_complementaires', 
                      'Etablissement recevant du public de 5ème catégorie soumis uniquement à l\'article PE24§1 du règlement de sécurité.');
                  }
                }}
                className="form-select"
              >
                <option value="">Sélectionner un modèle...</option>
                <option value="standard">Texte standard</option>
                <option value="erp">Modèle ERP</option>
                <option value="industriel">Modèle industriel</option>
                <option value="tertiaire">Modèle tertiaire</option>
              </select>
            </div>

            <div className="form-group">
              <label>Renseignements complémentaires</label>
              <textarea
                value={formData.renseignements_complementaires}
                onChange={(e) => updateField('renseignements_complementaires', e.target.value)}
                rows={5}
                placeholder="Informations complémentaires..."
                className="form-textarea"
              />
              <p className="helper-text">
                Informations supplémentaires qui apparaîtront sur la page 4 du rapport PDF
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="etape-actions">
        <button 
          className="btn btn-secondary" 
          onClick={onBack}
          disabled={isSaving}
        >
          Annuler
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? '💾 Sauvegarde...' : '💾 Sauvegarder et continuer'}
        </button>
      </div>
    </div>
  );
}

export default Etape1;